// Third Party
import * as React from 'react';
import { Formik, Form as FormikForm, FormikHelpers } from 'formik';
import parsePhoneNumber from 'libphonenumber-js';
import { v4 as uuid4 } from 'uuid';

// Utils
import { useCreateLeadMutation } from '@gql';
import { FormSchema, formSchema, initialValues } from './form-schema';
import { useNotification } from '@contexts/use-notification';
import { pmsCompanies } from '@utils/pms-companies';
import { useAnalytics } from '@contexts/use-analytics';

// UI
import { Button } from '@components/button';
import { FormikInput } from '@components/form/formik-input';
import { Typography } from '@components/typography';
import { FormField } from '@components/form/form-field';
import { SelectAutocomplete } from '@components/form/select-autocomplete';
import { FieldMessageType } from '@components/form/field';

type Props = {};

export const RequestACall: React.FC<Props> = () => {
  const [createLeadMutation] = useCreateLeadMutation();
  const { setNotification } = useNotification();
  const { emitGenerateLeadEvent } = useAnalytics();

  const onSubmit = async (
    {
      firstName,
      lastName,
      email,
      phoneNumber,
      company,
      website,
      numberOfListings,
      pmsSoftware,
    }: FormSchema,
    helpers: FormikHelpers<FormSchema>
  ) => {
    const _phoneNumber = parsePhoneNumber(phoneNumber, 'US')?.number;

    if (!_phoneNumber) throw new Error('phone number is not valid');
    if (typeof numberOfListings !== 'number')
      throw new Error('numberOfListings is not valid');

    const response = await createLeadMutation({
      variables: {
        input: {
          firstName,
          lastName,
          email,
          company,
          phone: _phoneNumber,
          website,
          numberOfListings,
          pmsSoftware: pmsSoftware?.value || '',
        },
      },
    });

    if (response.data?.createLead.__typename === 'LeadCreatedMessage') {
      emitGenerateLeadEvent({
        source: 'request_a_call_form',
        account_name: company,
      });

      setNotification({
        uniqueId: uuid4(),
        message: 'We will be in touch!',
        title: 'Success',
        type: 'success',
      });
      helpers.resetForm();
    } else {
      setNotification({
        uniqueId: uuid4(),
        message: 'Something went wrong',
        title: 'Error',
        type: 'error',
      });
    }
  };

  return (
    <div className="body-container">
      <div className="block">
        <div className="max-w-7xl mx-auto px-4 sm:pt-16 sm:px-6 xl:px-8">
          <div className="text-center">
            <Typography
              variant="title1"
              component="h1"
              className="text-sm text-center mx-auto relative w-full px-4 sm:px-8 mt-4 sm:mt-0"
            >
              Request A Call
            </Typography>
            <p className="max-w-2xl mx-4 sm:mx-auto text-xl text-gray-main mt-2 sm:mt-1">
              Please fill out the information below and we will give you a call!
            </p>
          </div>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, setFieldValue, values, touched }) => {
          return (
            <FormikForm>
              <div className="grid grid-cols-4 gap-4 max-w-lg mx-auto py-10">
                <div className="col-span-4">
                  <FormikInput
                    type="text"
                    label="First Name"
                    name="firstName"
                    id="firstName"
                  />
                </div>

                <div className="col-span-4">
                  <FormikInput
                    type="text"
                    label="Last Name"
                    name="lastName"
                    id="lastName"
                  />
                </div>

                <div className="col-span-4">
                  <FormikInput
                    type="email"
                    label="Email"
                    name="email"
                    id="email"
                  />
                </div>

                <div className="col-span-4">
                  <FormikInput
                    type="text"
                    label="Phone Number"
                    name="phoneNumber"
                    id="phoneNumber"
                  />
                </div>

                <div className="col-span-4">
                  <FormField
                    message={
                      !!errors.pmsSoftware && !!touched.pmsSoftware
                        ? {
                            type: FieldMessageType.ERROR,
                            content: errors.pmsSoftware as string,
                          }
                        : undefined
                    }
                  >
                    <SelectAutocomplete
                      isInvalid={!!errors.pmsSoftware && !!touched.pmsSoftware}
                      label="PM Software"
                      name="pmsSoftware"
                      noResultsLabel="No results"
                      options={pmsCompanies.map((company) => ({
                        label: company,
                        value: company,
                      }))}
                      showLabel={true}
                      value={values.pmsSoftware}
                      onChange={(_name, value) => {
                        setFieldValue('pmsSoftware', value);
                      }}
                    />
                  </FormField>
                </div>

                <div className="col-span-4">
                  <FormikInput
                    type="number"
                    label="Number of Properties"
                    id="numberOfListings"
                    name="numberOfListings"
                  />
                </div>

                <div className="col-span-4">
                  <FormikInput
                    type="text"
                    label="Company Name"
                    name="company"
                    id="company"
                  />
                </div>

                <div className="col-span-4">
                  <FormikInput
                    type="text"
                    label="Company Website"
                    name="website"
                    id="website"
                    placeholder="Ex. https://www.example.com"
                    placeholderPosition="below-field"
                  />
                </div>

                <div className="col-span-4 flex flex-col-reverse sm:flex-row sm:justify-end justify-center">
                  <Button
                    data-testid="signup-form-button"
                    type="submit"
                    size="lg"
                    isFullWidth={true}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    <div className="flex justify-center w-full">Submit</div>
                  </Button>
                </div>
              </div>
            </FormikForm>
          );
        }}
      </Formik>
    </div>
  );
};
